import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSearchRepository } from "@/features/documents/hooks/use-documents";
import { useTranslation } from "@/features/utils/i18n";
import { FileMagnifyingGlassIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { PageLayout } from "../../layout";

export function AskRepositoryPage() {
  const { t } = useTranslation("documents");
  const { company: clientId } = useParams<{ company: string }>();
  const [query, setQuery] = useState("");
  const { mutate: search, data, isPending } = useSearchRepository();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || !clientId) return;

    search({
      query: query.trim(),
      options: { limit: 5 },
    });
  };

  const results = data?.results || [];
  const sources = Array.from(
    new Map(results.map((r) => [r.document.id, r.document.filename])).entries(),
  );

  return (
    <PageLayout
      width="medium"
      title={t("ask.title")}
      description={t("ask.description")}
    >
      <div className="space-y-8">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <FileMagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("ask.placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10"
              disabled={isPending}
            />
          </div>
          <Button type="submit" disabled={isPending || !query.trim()}>
            {isPending ? t("ask.searching") : t("ask.search")}
          </Button>
        </form>

        {/* Sources */}
        {sources.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {t("ask.sources", { count: sources.length })}
            </h3>
            <div className="flex flex-wrap gap-2">
              {sources.map(([id, filename]) => (
                <Badge key={id} variant="secondary">
                  {filename}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">
              {t("ask.results", { count: results.length })}
            </h3>
            <div className="space-y-3">
              {results.map((result) => (
                <Card key={result.chunk.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm font-medium">
                        {result.document.filename}
                      </CardTitle>
                      <Badge variant="outline" className="ml-2">
                        {t("ask.match", {
                          percent: Math.round(result.chunk.similarity * 100),
                        })}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {result.chunk.content}
                    </p>
                    {result.chunk.metadata &&
                      Object.keys(result.chunk.metadata).length > 0 && (
                        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                          {t("ask.chunk", {
                            index: result.chunk.chunk_index + 1,
                          })}
                          TODO
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!results.length && !isPending && query && data && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("ask.noResults")}</p>
          </div>
        )}

        {!query && !data && (
          <div className="text-center py-12 text-muted-foreground">
            <p>{t("ask.emptyState")}</p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
